import { TFunction } from "i18next";
import {
  ExpiringLeaseBucket,
  ExpiringLeasesResult,
} from "~/types/finance";

export const DEFAULT_EXPIRING_LEASE_WINDOWS_DAYS = [30, 90, 180] as const;
export const MAX_EXPIRING_LEASE_WINDOWS = 5;
export const MAX_EXPIRING_LEASE_WINDOW_DAYS = 365;

export function normalizeExpiringLeaseWindows(
  windowsDays?: number[] | string | null,
): number[] {
  let raw: number[] = [];

  if (typeof windowsDays === "string") {
    raw = windowsDays
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((value) => Number.isFinite(value));
  } else if (Array.isArray(windowsDays)) {
    raw = windowsDays.filter((value) => Number.isFinite(value));
  }

  if (raw.length === 0) {
    return [...DEFAULT_EXPIRING_LEASE_WINDOWS_DAYS];
  }

  const normalized = Array.from(
    new Set(raw.map((value) => Math.floor(value))),
  )
    .filter((value) => value > 0 && value <= MAX_EXPIRING_LEASE_WINDOW_DAYS)
    .sort((a, b) => a - b)
    .slice(0, MAX_EXPIRING_LEASE_WINDOWS);

  return normalized.length > 0
    ? normalized
    : [...DEFAULT_EXPIRING_LEASE_WINDOWS_DAYS];
}

export function formatExpiringWindowsDaysParam(windowsDays: number[]): string {
  return normalizeExpiringLeaseWindows(windowsDays).join(",");
}

export function getExpiringBucket(
  expiringLeases: ExpiringLeasesResult,
  days: number,
): ExpiringLeaseBucket | undefined {
  return expiringLeases.buckets.find((bucket) => bucket.days === days);
}

export function getExpiringCount(
  expiringLeases: ExpiringLeasesResult,
  days: number,
): number {
  return getExpiringBucket(expiringLeases, days)?.count ?? 0;
}

export function getMaxExpiringWindowDays(
  expiringLeases: ExpiringLeasesResult,
): number {
  return expiringLeases.windowsDays[expiringLeases.windowsDays.length - 1] ?? 0;
}

export function hasExpiringLeases(expiringLeases: ExpiringLeasesResult): boolean {
  return expiringLeases.buckets.some((bucket) => bucket.count > 0);
}

export function emptyExpiringLeasesResult(
  windowsDays: number[] = [...DEFAULT_EXPIRING_LEASE_WINDOWS_DAYS],
): ExpiringLeasesResult {
  const normalized = normalizeExpiringLeaseWindows(windowsDays);
  return {
    windowsDays: normalized,
    buckets: [
      { days: 0, items: [], count: 0 },
      ...normalized.map((days) => ({ days, items: [], count: 0 })),
    ],
  };
}

export function formatExpiringWindowLabel(
  days: number,
  t: TFunction<"translation">,
): string {
  if (days === 0) return t("Expired");
  if (days === 30) return t("Expiring_within_1_month");
  if (days === 90) return t("Expiring_within_3_months");
  if (days === 180) return t("Expiring_within_6_months");
  return t("Expiring_within_days", { days });
}

export function formatExpiringWindowTabLabel(
  days: number,
  t: TFunction<"translation">,
): string {
  if (days === 0) return t("Expired");
  if (days === 30) return t("one_month");
  if (days === 90) return t("3_months");
  if (days === 180) return t("6_months");
  return t("Expiring_window_days_short", { days });
}
